/**
 * Scout v0 - criteria composer TanStack Query hooks (P16).
 *
 * Mirrors the P14/P15 backend compose flow:
 *   compose -> propose -> confirm -> preview -> launch
 *
 * Backend enforces "no launch without confirmed loose roles" (returns 409 with
 * unconfirmedRoles). The UI must additionally NOT offer a launch path that
 * bypasses the preview screen - the launch mutation is only invoked from the
 * preview step.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, fetchApi } from './api';
import { useAuth } from './auth';
import {
  appIdentityScope,
  captureMutationAdmission,
  scopeQueryKey,
  useSyncIdentityScope,
} from './identity-scope';
import {
  SCOUT_COMPOSE_CONFIRM_ENDPOINT,
  SCOUT_COMPOSE_ENDPOINT,
  SCOUT_COMPOSE_LAUNCH_ENDPOINT,
  SCOUT_COMPOSE_PREVIEW_ENDPOINT,
  SCOUT_COMPOSE_PROPOSE_ENDPOINT,
  SCOUT_CRITERIA_PROFILE_ENDPOINT,
  SCOUT_RUNS_ENDPOINT,
  SCOUT_SUPPRESSIONS_ADD_ENDPOINT,
  SCOUT_SUPPRESSIONS_ENDPOINT,
  SCOUT_SUPPRESSIONS_REMOVE_ENDPOINT,
  SCOUT_TEMPLATES_ENDPOINT,
  scoutExportGenerateEndpoint,
  scoutExportHistoryEndpoint,
  scoutExportPreviewEndpoint,
  scoutQaDecisionEndpoint,
  scoutQaQueueEndpoint,
  scoutQaSummaryEndpoint,
  scoutRunDetailEndpoint,
  scoutRunResumeEndpoint,
  scoutSuppressionPromoteEndpoint,
  scoutSuppressionTypeEndpoint,
  scoutTemplateArchiveEndpoint,
  scoutTemplateDetailEndpoint,
} from './api-contract';
import type { SuppressionListType } from './api-contract';
import type {
  ClassTemplate,
  ComposeRequest,
  ComposeResponse,
  ConfirmResponse,
  ConfirmedRoleEdit,
  CriteriaProfile,
  ExportGenerateResponse,
  ExportHistoryResponse,
  ExportPreviewResponse,
  ExportScope,
  LaunchRequest,
  LaunchResponse,
  PreviewResponse,
  PromotedSuppression,
  ProposeResponse,
  QaDecisionRequest,
  QaDecisionResponse,
  QaQueueResponse,
  QaSessionSummaryResponse,
  RoleSpec,
  RunDetail,
  RunSummary,
  RunsListResponse,
  SuppressionAddRequest,
  SuppressionClearResponse,
  SuppressionLists,
  SuppressionRemoveRequest,
  SuppressionRemoveResponse,
  SuppressionReplaceRequest,
} from '@/types/scout';

/* ─────────────────────────────────────────────────────────────────────── */
/* Query keys — APP-W01c identity-scoped private keys                       */
/* ─────────────────────────────────────────────────────────────────────── */

/**
 * The module-level IdentityScope for the W01c query seam. This is the
 * SINGLE catalogue of confirmed identities the cache scoping consults;
 * `QueryScopeGateway` (rendered inside the AuthProvider tree, mounted in
 * main.tsx) advances it only on server-confirmed identity commits so
 * probe/live identity and cache scoping share the same generation numbers.
 */

/**
 * Logical key factories — the identity tuple is supplied by the consuming
 * hook at creation time (never shared across identities).
 */
export const scoutQueryKeys = {
  profile: ['scout', 'profile'] as const,
  templates: ['scout', 'templates'] as const,
  template: (id: string) => ['scout', 'templates', id] as const,
  runs: ['scout', 'runs'] as const,
  run: (id: string) => ['scout', 'runs', id] as const,
  qaQueue: (runId: string) => ['scout', 'qa', runId, 'queue'] as const,
  qaSummary: (runId: string) => ['scout', 'qa', runId, 'summary'] as const,
  exportPreview: (runId: string, scope: ExportScope) => ['scout', 'export', runId, 'preview', scope] as const,
  exportHistory: (runId: string) => ['scout', 'export', runId, 'history'] as const,
  suppressions: ['scout', 'suppressions'] as const,
};

/**
 * APP-W01c: scope a logical private key by the CURRENT confirmed identity.
 * The tuple ordering is [resource scope discriminator, identity tuple…
 * logical key]. Three-facet prefix discipline:
 *
 * - RUN LIST vs RUN DETAIL stay distinct (list key never matches a run
 *   detail invalidate/refetch);
 * - QA and EXPORT scopes never invalidate each other's keys;
 * - the identity tuple in front means a cancel/removal across identities
 *   cannot cross user/workspace/generation boundaries — a private key for
 *   identity A is unreachable by any consumer scoped to identity B, and a
 *   permission re-confirmation (byte-identical ids, new generation) is a
 *   different subtree so stale data never repopulates.
 *
 * Returns null when no identity is confirmed: private queries are DISABLED
 * before authenticated identity (fail closed).
 */
export function scopedScoutQueryKey(logical: readonly unknown[]): readonly unknown[] | null {
  return scopeQueryKey(appIdentityScope, logical);
}
/* ──────────────────────────────────────────────────────────────────────── */
/* APP-W01c hook seam                                                        */
/*                                                                           */
/* Every private hook:                                                       */
/* - keys are scoped [identity-tuple, logical key] at CREATION via            */
/*   scopedScoutQueryKey; a null tuple (no confirmed identity) DISABLES       */
/*   the query (fail closed — private data never loads unauthenticated);     */
/* - the TanStack query signal is forwarded to the transport so a            */
/*   transition invalidation (generation-first) aborts the wire request;    */
/* - mutation ADMISSION captures the identity state before the request       */
/*   (no request leaves without a confirmed current identity);               */
/* - every success/error/settled cache effect is fenced to the generation    */
/*   captured at admission (a stale callback can never toast, navigate or    */
/*   alter cache state);                                                     */
/* - mutations never retry.                                                  */
/* ──────────────────────────────────────────────────────────────────────── */

/** Sentinel marker prefix for disabled (identity-less) scout keys. */
const SCOUT_SCOPE_TAG = 'scout-scope-1';

function useScoutQueryContext() {
  const { isAuthenticated, user, org } = useAuth();
  const enabled = isAuthenticated && !!user?.userId && !!org?.orgId;
  // Sync the app scope with the CONFIRMED identity before computing keys.
  // advance() is a no-op for an identical (ids, generation) tuple, so
  // re-renders never bump the generation; only a new confirmed commit does.
  useSyncIdentityScope(enabled && user ? user : null, enabled && org ? org : null);
  const generation = appIdentityScope.current().generation;
  return {
    scopedKey: (logical: readonly unknown[]): readonly unknown[] | null =>
      enabled ? scopedScoutQueryKey(logical) : null,
    enabled,
    generation,
  };
}

/** Session-required classification for a mutation admitted without identity. */
function sessionRequiredError(): ApiError {
  return new ApiError('Sign-in required. Please authenticate.', 401, false, 'SESSION_REQUIRED');
}

/**
 * APP-W01c: invalidate the CURRENT identity's subtree for a logical prefix.
 * The logical prefix (e.g. scoutQueryKeys.runs) is scoped first, so the
 * invalidate covers the runs list AND nested run details of the confirmed
 * identity, but never a foreign identity's subtree and never a foreign
 * scope (three-facet prefix discipline). No-op (fenced) without identity.
 */
function runScopedInvalidation(
  queryClient: ReturnType<typeof useQueryClient>,
  scopedKey: (logical: readonly unknown[]) => readonly unknown[] | null,
  logical: readonly unknown[]
): Promise<void> {
  const key = scopedKey(logical);
  if (!key) return Promise.resolve();
  return queryClient.invalidateQueries({ queryKey: key }).then(() => undefined);
}

/* ──────────────────────────────────────────────────────────────────────── */
/* Layer 1 - workspace criteria profile (read-only in the composer)          */
/* ──────────────────────────────────────────────────────────────────────── */

export function useScoutProfile() {
  const { scopedKey, enabled } = useScoutQueryContext();
  const key = scopedKey(scoutQueryKeys.profile);
  return useQuery<CriteriaProfile, ApiError>({
    queryKey: key ?? [SCOUT_SCOPE_TAG, 'disabled'],
    queryFn: ({ signal }) => fetchApi<CriteriaProfile>(SCOUT_CRITERIA_PROFILE_ENDPOINT, { signal }),
    enabled: enabled && !!key,
  });
}

interface UpdateProfilePayload {
  identity: string;
}

/**
 * Update the workspace identity. Sends identity only — the backend PUT
 * preserves the stored suppression lists when the field is omitted, so this
 * never races the typed-list suppression mutations.
 */
export function useUpdateScoutProfile() {
  const { scopedKey, enabled } = useScoutQueryContext();
  const queryClient = useQueryClient();
  return useMutation<CriteriaProfile, ApiError, UpdateProfilePayload>({
    mutationFn: (payload) => {
      // Mutation admission (W01c-C1/C2): capture BEFORE the wire call; the
      // captured admission, not a settled-time identity lookup, owns the
      // settle-time cache write.
      const admission = captureMutationAdmission(appIdentityScope);
      if (!enabled || !scopedKey(scoutQueryKeys.profile) || !admission) {
        return Promise.reject(sessionRequiredError());
      }
      return fetchApi<CriteriaProfile>(SCOUT_CRITERIA_PROFILE_ENDPOINT, {
        method: 'PUT',
        body: JSON.stringify(payload),
      }).then((data) => {
        // Settle-time fence on THIS call's admission: a stale A response
        // never writes under B.
        if (admission.isCurrent()) {
          queryClient.setQueryData<CriteriaProfile>(admission.key(scoutQueryKeys.profile), data);
        }
        return data;
      });
    },
  });
}

/* ──────────────────────────────────────────────────────────────────────── */
/* Layer 2 - class templates (CRUD)                                          */
/* ──────────────────────────────────────────────────────────────────────── */

export function useScoutTemplates() {
  const { scopedKey, enabled } = useScoutQueryContext();
  const key = scopedKey(scoutQueryKeys.templates);
  return useQuery<ClassTemplate[], ApiError>({
    queryKey: key ?? [SCOUT_SCOPE_TAG, 'disabled'],
    queryFn: ({ signal }) => fetchApi<ClassTemplate[]>(SCOUT_TEMPLATES_ENDPOINT, { signal }),
    enabled: enabled && !!key,
  });
}

interface CreateTemplatePayload {
  name: string;
  archetype?: string;
  roles?: RoleSpec[];
  keywords?: string[];
}

export function useCreateScoutTemplate() {
  const { enabled } = useScoutQueryContext();
  const queryClient = useQueryClient();
  return useMutation<ClassTemplate, ApiError, CreateTemplatePayload>({
    mutationFn: (payload) => {
      // Mutation admission (W01c-C1/C2): captured BEFORE the wire call.
      const admission = captureMutationAdmission(appIdentityScope);
      if (!enabled || !admission) return Promise.reject(sessionRequiredError());
      return fetchApi<ClassTemplate>(SCOUT_TEMPLATES_ENDPOINT, {
        method: 'POST',
        body: JSON.stringify(payload),
      }).then((created) => {
        // Settle-time fence on THIS call's admission: stale A never writes
        // its optimistic append under B.
        if (admission.isCurrent()) {
          const key = admission.key(scoutQueryKeys.templates);
          // Optimistic: append the new template to the cached list.
          queryClient.setQueryData<ClassTemplate[]>(key, (old) =>
            old ? [...old, created] : [created],
          );
        }
        return created;
      });
    },
  });
}

interface UpdateTemplatePayload {
  templateId: string;
  name?: string;
  archetype?: string;
  roles?: RoleSpec[];
  keywords?: string[];
}

export function useUpdateScoutTemplate() {
  const { enabled } = useScoutQueryContext();
  const queryClient = useQueryClient();
  return useMutation<ClassTemplate, ApiError, UpdateTemplatePayload>({
    mutationFn: ({ templateId, ...payload }) => {
      // Mutation admission (W01c-C1/C2): captured BEFORE the wire call.
      const admission = captureMutationAdmission(appIdentityScope);
      if (!enabled || !admission) return Promise.reject(sessionRequiredError());
      return fetchApi<ClassTemplate>(scoutTemplateDetailEndpoint(templateId), {
        method: 'PUT',
        body: JSON.stringify(payload),
      }).then((updated) => {
        // Settle-time fence on THIS call's admission: stale A never writes
        // under B.
        if (admission.isCurrent()) {
          const key = admission.key(scoutQueryKeys.templates);
          queryClient.setQueryData<ClassTemplate[]>(key, (old) =>
            old ? old.map((t) => (t.id === updated.id ? updated : t)) : [updated],
          );
        }
        return updated;
      });
    },
  });
}

export function useArchiveScoutTemplate() {
  const { enabled } = useScoutQueryContext();
  const queryClient = useQueryClient();
  return useMutation<ClassTemplate, ApiError, string>({
    mutationFn: (templateId) => {
      // Mutation admission (W01c-C1/C2): captured BEFORE the wire call.
      const admission = captureMutationAdmission(appIdentityScope);
      if (!enabled || !admission) return Promise.reject(sessionRequiredError());
      return fetchApi<ClassTemplate>(scoutTemplateArchiveEndpoint(templateId), {
        method: 'POST',
      }).then((archived) => {
        // Settle-time fence on THIS call's admission: stale A never writes
        // under B.
        if (admission.isCurrent()) {
          const key = admission.key(scoutQueryKeys.templates);
          // Remove the archived template from the active list.
          queryClient.setQueryData<ClassTemplate[]>(key, (old) =>
            old ? old.filter((t) => t.id !== archived.id) : [],
          );
        }
        return archived;
      });
    },
  });
}

/* ──────────────────────────────────────────────────────────────────────── */
/* Compose flow                                                              */
/* ──────────────────────────────────────────────────────────────────────── */

/** POST /scout/compose - merge layer 1 + template + specifics (no AI). */
export function useCompose() {
  const { enabled } = useScoutQueryContext();
  return useMutation<ComposeResponse, ApiError, ComposeRequest>({
    mutationFn: (payload) => {
      if (!enabled) return Promise.reject(sessionRequiredError());
      return fetchApi<ComposeResponse>(SCOUT_COMPOSE_ENDPOINT, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
    },
  });
}

/** POST /scout/compose/propose - AI synonym proposals for loose roles. */
export function usePropose() {
  const { enabled } = useScoutQueryContext();
  return useMutation<ProposeResponse, ApiError, { composed: ComposeResponse['composed'] }>({
    mutationFn: (payload) => {
      if (!enabled) return Promise.reject(sessionRequiredError());
      return fetchApi<ProposeResponse>(SCOUT_COMPOSE_PROPOSE_ENDPOINT, {
        method: 'POST',
        body: JSON.stringify({ composed: payload.composed }),
      });
    },
  });
}

interface ConfirmPayload {
  composed: ComposeResponse['composed'];
  edits: ConfirmedRoleEdit[];
  templateId: string;
}

/** POST /scout/compose/confirm - apply confirmed synonyms + persist to template. */
export function useConfirm() {
  const { enabled } = useScoutQueryContext();
  return useMutation<ConfirmResponse, ApiError, ConfirmPayload>({
    mutationFn: (payload) => {
      if (!enabled) return Promise.reject(sessionRequiredError());
      return fetchApi<ConfirmResponse>(SCOUT_COMPOSE_CONFIRM_ENDPOINT, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
    },
  });
}

/** POST /scout/compose/preview - full composed spec for pre-launch display (pure read). */
export function usePreview() {
  const { enabled } = useScoutQueryContext();
  return useMutation<PreviewResponse, ApiError, { composed: ComposeResponse['composed'] }>({
    mutationFn: (payload) => {
      if (!enabled) return Promise.reject(sessionRequiredError());
      return fetchApi<PreviewResponse>(SCOUT_COMPOSE_PREVIEW_ENDPOINT, {
        method: 'POST',
        body: JSON.stringify({ composed: payload.composed }),
      });
    },
  });
}

/**
 * POST /scout/compose/launch - record snapshot + execute the pipeline.
 *
 * CONSTRAINT: this is ONLY called from the preview screen after the operator
 * has confirmed all loose roles. The backend rejects unconfirmed specs (409),
 * but the UI must not offer a path to launch that skips the preview step.
 *
 * APP-W01c: launch invalidates ONLY the scoped runs subtree (list is the
 * runs prefix; matching launch refreshes list/detail, not QA or export
 * scopes) for the CURRENT identity, never a foreign one.
 */
export function useLaunch() {
  const { enabled } = useScoutQueryContext();
  const queryClient = useQueryClient();
  return useMutation<LaunchResponse, ApiError, LaunchRequest>({
    mutationFn: (payload) => {
      // Mutation admission (W01c-C1/C2): captured BEFORE the wire call; the
      // captured admission fences the settle-time invalidation.
      const admission = captureMutationAdmission(appIdentityScope);
      if (!enabled || !admission) return Promise.reject(sessionRequiredError());
      return fetchApi<LaunchResponse>(SCOUT_COMPOSE_LAUNCH_ENDPOINT, {
        method: 'POST',
        body: JSON.stringify(payload),
      }).then((data) => {
        // Settle-time fence on THIS admission: a stale A launch never
        // invalidates under B (zero cache mutation for departed identity).
        if (admission.isCurrent()) {
          void runScopedInvalidation(queryClient, admission.key, scoutQueryKeys.runs);
        }
        return data;
      });
    },
  });
}

/* ──────────────────────────────────────────────────────────────────────── */
/* Run status (P17)                                                          */
/* ──────────────────────────────────────────────────────────────────────── */

const RUN_POLL_INTERVAL_MS = 5000;

/** GET /scout/runs - run history, polls while any run is still in flight. */
export function useScoutRuns() {
  const { scopedKey, enabled } = useScoutQueryContext();
  const key = scopedKey(scoutQueryKeys.runs);
  return useQuery<RunSummary[], ApiError>({
    queryKey: key ?? [SCOUT_SCOPE_TAG, 'disabled'],
    queryFn: ({ signal }) =>
      fetchApi<RunsListResponse>(SCOUT_RUNS_ENDPOINT, { signal }).then((res) => res.runs),
    refetchInterval: (query) =>
      query.state.data?.some((run) => run.status === 'running') ? RUN_POLL_INTERVAL_MS : false,
    enabled: enabled && !!key,
  });
}

/** GET /scout/runs/:runId - one run, polls while the run is still in flight. */
export function useScoutRun(runId: string) {
  const { scopedKey, enabled } = useScoutQueryContext();
  const key = scopedKey(scoutQueryKeys.run(runId));
  return useQuery<RunDetail, ApiError>({
    queryKey: key ?? [SCOUT_SCOPE_TAG, 'disabled'],
    queryFn: ({ signal }) => fetchApi<RunDetail>(scoutRunDetailEndpoint(runId), { signal }),
    refetchInterval: (query) =>
      query.state.data?.status === 'running' ? RUN_POLL_INTERVAL_MS : false,
    enabled: enabled && !!key,
  });
}

/**
 * POST /scout/runs/:runId/resume - resume a FAILED run from its checkpoints
 * (P26). Wraps the P07 resume lane (`pnpm scout:resume`) - one lane, two
 * interfaces. Checkpointed stages (incl. stage-7 contact routing) are never
 * re-run, so a resume never re-spends provider credits. Only offered on
 * failed runs; the backend rejects running/completed with 409.
 */
export function useResumeRun(runId: string) {
  const { enabled } = useScoutQueryContext();
  const queryClient = useQueryClient();
  return useMutation<LaunchResponse, ApiError, void>({
    mutationFn: () => {
      // Mutation admission (W01c-C1/C2): captured BEFORE the wire call.
      const admission = captureMutationAdmission(appIdentityScope);
      if (!enabled || !admission) return Promise.reject(sessionRequiredError());
      return fetchApi<LaunchResponse>(scoutRunResumeEndpoint(runId), { method: 'POST' }).then(
        (data) => {
          // Settled-side effect runs under THIS admission only — a stale A
          // resume never invalidates B's subtree in either settle branch.
          if (admission.isCurrent()) {
            // Refresh the run + list either way - a failed resume still moved
            // the status through 'running' back to 'failed'. Run + list only.
            void runScopedInvalidation(queryClient, admission.key, scoutQueryKeys.run(runId));
            void runScopedInvalidation(queryClient, admission.key, scoutQueryKeys.runs);
          }
          return data;
        },
        (err: unknown) => {
          if (admission.isCurrent()) {
            void runScopedInvalidation(queryClient, admission.key, scoutQueryKeys.run(runId));
            void runScopedInvalidation(queryClient, admission.key, scoutQueryKeys.runs);
          }
          throw err;
        },
      );
    },
  });
}

/* ──────────────────────────────────────────────────────────────────────── */
/* Human QA gate (P18) — one lane, two interfaces (CLI + UI)                 */
/* ──────────────────────────────────────────────────────────────────────── */

/** GET /scout/qa/:runId/queue — the per-run QA queue (graded leads + review state). */
export function useQaQueue(runId: string) {
  const { scopedKey, enabled } = useScoutQueryContext();
  const key = scopedKey(scoutQueryKeys.qaQueue(runId));
  return useQuery<QaQueueResponse, ApiError>({
    queryKey: key ?? [SCOUT_SCOPE_TAG, 'disabled'],
    queryFn: ({ signal }) => fetchApi<QaQueueResponse>(scoutQaQueueEndpoint(runId), { signal }),
    enabled: enabled && !!key,
  });
}

/**
 * POST /scout/qa/:runId/decision — submit one lead's decision.
 *
 * OVERRIDE-REQUIRES-REASON: the backend rejects an override (finalGrade !==
 * aiGrade) without a non-empty note with 400 { code: 'override-requires-reason' }.
 * The UI must not offer a submit path that bypasses the reason field on override.
 * On success the queue cache is invalidated so the reviewed count updates.
 *
 * APP-W01c: invalidations use the scoped [scout-l QA, scoped export]
 * factories — the matching QA/export keys refresh ONLY for the current
 * identity; no foreign scope or foreign identity is touched.
 */
export function useSubmitQaDecision(runId: string) {
  const { enabled } = useScoutQueryContext();
  const queryClient = useQueryClient();
  return useMutation<QaDecisionResponse, ApiError, QaDecisionRequest>({
    mutationFn: (payload) => {
      // Mutation admission (W01c-C1/C2): captured BEFORE the wire call.
      const admission = captureMutationAdmission(appIdentityScope);
      if (!enabled || !admission) return Promise.reject(sessionRequiredError());
      return fetchApi<QaDecisionResponse>(scoutQaDecisionEndpoint(runId), {
        method: 'POST',
        body: JSON.stringify(payload),
      }).then((data) => {
        // Settle-time fence on THIS admission: a stale A QA decision never
        // invalidates B's QA/export subtree.
        if (admission.isCurrent()) {
          // Invalidate the queue + summary so the reviewed count + session state update.
          void runScopedInvalidation(queryClient, admission.key, ['scout', 'qa', runId]);
          // Human decisions change usable export membership and final grades.
          void runScopedInvalidation(queryClient, admission.key, ['scout', 'export', runId]);
        }
        return data;
      });
    },
  });
}

/** GET /scout/qa/:runId/summary — session summary (counts, overrides, guard-flagged rules). */
export function useQaSummary(runId: string) {
  const { scopedKey, enabled } = useScoutQueryContext();
  const key = scopedKey(scoutQueryKeys.qaSummary(runId));
  return useQuery<QaSessionSummaryResponse, ApiError>({
    queryKey: key ?? [SCOUT_SCOPE_TAG, 'disabled'],
    queryFn: ({ signal }) => fetchApi<QaSessionSummaryResponse>(scoutQaSummaryEndpoint(runId), { signal }),
    enabled: enabled && !!key,
  });
}

/**
 * POST /scout/suppressions/:id/promote — §1.6 human-approval gate. Promotes a
 * QA-created 'pending_review' rule (held by the P06 volume guard) to 'active'.
 * Backend: 404 cross-org, 409 when the rule is not pending_review. On success
 * the QA summary + suppressions caches are invalidated so the pending badge
 * flips to active without a manual reload. On 409 the summary is also
 * invalidated — the rule is no longer pending, so the stale row must resync.
 */
export function usePromoteSuppression(runId: string) {
  const { enabled } = useScoutQueryContext();
  const queryClient = useQueryClient();
  return useMutation<PromotedSuppression, ApiError, string>({
    mutationFn: (suppressionId) => {
      // Mutation admission (W01c-C1/C2): captured BEFORE the wire call.
      const admission = captureMutationAdmission(appIdentityScope);
      if (!enabled || !admission) return Promise.reject(sessionRequiredError());
      return fetchApi<PromotedSuppression>(scoutSuppressionPromoteEndpoint(suppressionId), {
        method: 'POST',
      }).then(
        (data) => {
          // Settle-time fence on THIS admission (success branch): stale A
          // never invalidates B's subtree.
          if (admission.isCurrent()) {
            void runScopedInvalidation(queryClient, admission.key, scoutQueryKeys.qaSummary(runId));
            void runScopedInvalidation(queryClient, admission.key, scoutQueryKeys.suppressions);
          }
          return data;
        },
        (error: unknown) => {
          // Settle-time fence on THIS admission (error branch): a stale A
          // callback may NOT invalidate B's subtree.
          if (error instanceof ApiError && error.status === 409 && admission.isCurrent()) {
            void runScopedInvalidation(queryClient, admission.key, scoutQueryKeys.qaSummary(runId));
          }
          throw error;
        },
      );
    },
  });
}

/* ──────────────────────────────────────────────────────────────────────── */
/* Export screen (P19) — one lane, two interfaces (CLI + UI)                 */
/* The export UI wraps the same P03 export lane the CLI uses. Write-back     */
/* semantics preserved: export does NOT re-write touch_history clocks;       */
/* re-export is idempotent (identical CSV + upserted history row).           */
/* ──────────────────────────────────────────────────────────────────────── */

/** GET /scout/export/:runId/preview?scope=ab|cd — pre-download column preview. */
export function useExportPreview(runId: string, scope: ExportScope) {
  const { scopedKey, enabled } = useScoutQueryContext();
  const key = scopedKey(scoutQueryKeys.exportPreview(runId, scope));
  return useQuery<ExportPreviewResponse, ApiError>({
    queryKey: key ?? [SCOUT_SCOPE_TAG, 'disabled'],
    queryFn: ({ signal }) => fetchApi<ExportPreviewResponse>(scoutExportPreviewEndpoint(runId, scope), { signal }),
    // The export panel mounts before a run is selected — don't fire with ''.
    // APP-W01c: ALSO disabled before a confirmed identity (fail closed).
    enabled: runId.length > 0 && enabled && !!key,
  });
}

/** POST /scout/export/:runId/generate?scope=ab|cd — generate/re-generate the CSV. */
export function useGenerateExport(runId: string, scope: ExportScope) {
  const { enabled } = useScoutQueryContext();
  const queryClient = useQueryClient();
  return useMutation<ExportGenerateResponse, ApiError, void>({
    mutationFn: () => {
      // Mutation admission (W01c-C1/C2): captured BEFORE the wire call.
      const admission = captureMutationAdmission(appIdentityScope);
      if (!enabled || !admission) return Promise.reject(sessionRequiredError());
      return fetchApi<ExportGenerateResponse>(scoutExportGenerateEndpoint(runId, scope), {
        method: 'POST',
      }).then((data) => {
        // Settle-time fence on THIS admission: stale A never invalidates
        // B's export subtree.
        if (admission.isCurrent()) {
          // Invalidate the history so the audit trail updates after generate.
          void runScopedInvalidation(queryClient, admission.key, ['scout', 'export', runId]);
        }
        return data;
      });
    },
  });
}

/** GET /scout/export/:runId/history — export audit trail (who/when/scope/rows). */
export function useExportHistory(runId: string) {
  const { scopedKey, enabled } = useScoutQueryContext();
  const key = scopedKey(scoutQueryKeys.exportHistory(runId));
  return useQuery<ExportHistoryResponse, ApiError>({
    queryKey: key ?? [SCOUT_SCOPE_TAG, 'disabled'],
    queryFn: ({ signal }) => fetchApi<ExportHistoryResponse>(scoutExportHistoryEndpoint(runId), { signal }),
    // The export panel mounts before a run is selected — don't fire with ''.
    enabled: runId.length > 0 && enabled && !!key,
  });
}

/* ──────────────────────────────────────────────────────────────────────── */
/* Workspace always-on suppression lists (P20) — §1.3 layer 1.               */
/* Bulk paste domains/emails into typed lists. Competitor entries are        */
/* review-first (locked nuance). Same suppressions JSONB the P14 profile     */
/* route owns; entries take effect at compose time (P15 merge).              */
/* ──────────────────────────────────────────────────────────────────────── */

/** GET /scout/suppressions — all always-on suppression lists. */
export function useScoutSuppressions() {
  const { scopedKey, enabled } = useScoutQueryContext();
  const key = scopedKey(scoutQueryKeys.suppressions);
  return useQuery<SuppressionLists, ApiError>({
    queryKey: key ?? [SCOUT_SCOPE_TAG, 'disabled'],
    queryFn: ({ signal }) => fetchApi<SuppressionLists>(SCOUT_SUPPRESSIONS_ENDPOINT, { signal }),
    enabled: enabled && !!key,
  });
}

/** POST /scout/suppressions/add — bulk add to one typed list. */
export function useAddSuppressions() {
  const { enabled } = useScoutQueryContext();
  const queryClient = useQueryClient();
  return useMutation<SuppressionLists, ApiError, SuppressionAddRequest>({
    mutationFn: (payload) => {
      // Mutation admission (W01c-C1/C2): captured BEFORE the wire call.
      const admission = captureMutationAdmission(appIdentityScope);
      if (!enabled || !admission) return Promise.reject(sessionRequiredError());
      return fetchApi<SuppressionLists>(SCOUT_SUPPRESSIONS_ADD_ENDPOINT, {
        method: 'POST',
        body: JSON.stringify(payload),
      }).then((lists) => {
        // Settle-time fence on THIS admission: stale A never writes under B.
        if (admission.isCurrent()) {
          queryClient.setQueryData<SuppressionLists>(
            admission.key(scoutQueryKeys.suppressions),
            lists,
          );
        }
        return lists;
      });
    },
  });
}

/** POST /scout/suppressions/remove — remove entries from one typed list. */
export function useRemoveSuppressions() {
  const { enabled } = useScoutQueryContext();
  const queryClient = useQueryClient();
  return useMutation<SuppressionRemoveResponse, ApiError, SuppressionRemoveRequest>({
    mutationFn: (payload) => {
      // Mutation admission (W01c-C1/C2): captured BEFORE the wire call.
      const admission = captureMutationAdmission(appIdentityScope);
      if (!enabled || !admission) return Promise.reject(sessionRequiredError());
      return fetchApi<SuppressionRemoveResponse>(SCOUT_SUPPRESSIONS_REMOVE_ENDPOINT, {
        method: 'POST',
        body: JSON.stringify(payload),
      }).then((res) => {
        const { removed: _removed, ...lists } = res;
        void _removed;
        // Settle-time fence on THIS admission: stale A never writes under B.
        if (admission.isCurrent()) {
          queryClient.setQueryData<SuppressionLists>(
            admission.key(scoutQueryKeys.suppressions),
            lists,
          );
        }
        return res;
      });
    },
  });
}

/** PUT /scout/suppressions/:type — replace one typed list outright. */
export function useReplaceSuppressions() {
  const { enabled } = useScoutQueryContext();
  const queryClient = useQueryClient();
  return useMutation<SuppressionLists, ApiError, { type: SuppressionListType; payload: SuppressionReplaceRequest }>({
    mutationFn: ({ type, payload }) => {
      // Mutation admission (W01c-C1/C2): captured BEFORE the wire call.
      const admission = captureMutationAdmission(appIdentityScope);
      if (!enabled || !admission) return Promise.reject(sessionRequiredError());
      return fetchApi<SuppressionLists>(scoutSuppressionTypeEndpoint(type), {
        method: 'PUT',
        body: JSON.stringify(payload),
      }).then((lists) => {
        // Settle-time fence on THIS admission: stale A never writes under B.
        if (admission.isCurrent()) {
          queryClient.setQueryData<SuppressionLists>(
            admission.key(scoutQueryKeys.suppressions),
            lists,
          );
        }
        return lists;
      });
    },
  });
}

/** DELETE /scout/suppressions/:type — clear one typed list. */
export function useClearSuppressions() {
  const { enabled } = useScoutQueryContext();
  const queryClient = useQueryClient();
  return useMutation<SuppressionClearResponse, ApiError, SuppressionListType>({
    mutationFn: (type) => {
      // Mutation admission (W01c-C1/C2): captured BEFORE the wire call.
      const admission = captureMutationAdmission(appIdentityScope);
      if (!enabled || !admission) return Promise.reject(sessionRequiredError());
      return fetchApi<SuppressionClearResponse>(scoutSuppressionTypeEndpoint(type), {
        method: 'DELETE',
      }).then((res) => {
        const { cleared: _cleared, ...lists } = res;
        void _cleared;
        // Settle-time fence on THIS admission: stale A never writes under B.
        if (admission.isCurrent()) {
          queryClient.setQueryData<SuppressionLists>(
            admission.key(scoutQueryKeys.suppressions),
            lists,
          );
        }
        return res;
      });
    },
  });
}
